import React, { useState, useEffect, useRef } from "react";
import globals from "web/script/modules/globals";
import { gettext } from "web/script/modules/django-i18n";
import classnames from "classnames";
import styles from "./ticker.css";

interface TickerTextProps {
    tickerText: string;
    isPresentational?: boolean;
}
export function TickerText({ tickerText, isPresentational = false }: TickerTextProps): JSX.Element {
    return (
        <span
            className={styles.scrollingText}
            role={isPresentational ? "presentation" : undefined}
            dangerouslySetInnerHTML={{
                __html: tickerText,
            }}
        />
    );
}

interface TickerProps {
    speed: number;
    className?: string;
}

export default function Ticker({ speed, className }: TickerProps): JSX.Element {
    const container = useRef<HTMLDivElement>(null);
    const text = useRef<HTMLElement>(null);
    const [repeatedTexts, setRepeatedTexts] = useState(0);
    const [textWidth, setTextWidth] = useState(0);
    const tickerText = gettext("homepage.module_today_lysts.title");

    useEffect(() => {
        function calculateAdditionalTexts(): void {
            if (!text.current || !container.current) {
                return;
            }
            const textNode = text.current;
            const containerNode = container.current;
            const containerWidth = containerNode.getBoundingClientRect().width;
            const textWidth = textNode.getBoundingClientRect().width;
            const additionalTexts = Math.ceil(containerWidth / textWidth);
            setRepeatedTexts(additionalTexts + 1);
            setTextWidth(textWidth);
        }

        calculateAdditionalTexts();

        globals.window.addEventListener("resize", calculateAdditionalTexts);

        return (): void => {
            globals.window.removeEventListener("resize", calculateAdditionalTexts);
        };
    }, [text, container]);

    return (
        <div ref={container} className={classnames(styles.textContainer, className)}>
            <span
                ref={text}
                className={classnames(styles.textTicker, {
                    [styles.beginAnimation]: repeatedTexts > 0,
                })}
                style={{ animationDuration: `${textWidth / speed}s` }}
            >
                <TickerText tickerText={tickerText} />
                <span className={styles.textFake}>
                    {Array.from({ length: repeatedTexts }).map((_, i) => (
                        <TickerText key={i} tickerText={tickerText} isPresentational={true} />
                    ))}
                </span>
            </span>
        </div>
    );
}
